/**
 * /payments — full payments list (AP disbursements + AR receipts).
 * Filter by direction and date range; click a row to drill into the detail page.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { paymentsListQueryOptions, partiesListQueryOptions } from "@/lib/queries";
import type { Party, Payment, PaymentDirection } from "@/lib/types";
import { useShortcut } from "@/lib/shortcuts";
import { PaymentsGrid } from "@/grids/PaymentsGrid";

export const Route = createFileRoute("/_auth/payments/")({
  component: PaymentsPage,
});

function fmt(n: string | number, currency = "CAD") {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      });
}


function PaymentsPage() {
  const navigate = useNavigate();

  const [direction, setDirection] = useState<PaymentDirection | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  // `n` jumps to the check-run flow (the only "new payment" entry point on
  // this page — single-payment creation lives off invoice detail screens).
  useShortcut("n", () => navigate({ to: "/payments/check-run" }), {
    label: "New payment (check run)",
  });
  // `/` focuses the filter search field.
  useShortcut("/", () => searchRef.current?.focus(), {
    label: "Focus search",
  });

  const paymentsQ = useQuery(
    paymentsListQueryOptions(direction ? { direction } : {}),
  );
  const payments: Payment[] = paymentsQ.data ?? [];

  const partiesQ = useQuery(partiesListQueryOptions());
  const partiesById = useMemo(() => {
    const m: Record<string, Party> = {};
    (partiesQ.data ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [partiesQ.data]);

  const filtered = useMemo(() => {
    return payments.filter((p) => {
      if (dateFrom && p.payment_date < dateFrom) return false;
      if (dateTo && p.payment_date > dateTo) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !p.payment_no.toLowerCase().includes(q) &&
          !(p.reference ?? "").toLowerCase().includes(q) &&
          !(p.memo ?? "").toLowerCase().includes(q) &&
          !(p.check_no ?? "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [payments, dateFrom, dateTo, search]);

  const totalOut = filtered
    .filter((p) => p.direction === "outgoing")
    .reduce((s, p) => s + parseFloat(p.amount), 0);
  const totalIn = filtered
    .filter((p) => p.direction === "incoming")
    .reduce((s, p) => s + parseFloat(p.amount), 0);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Payments
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            AP disbursements &amp; AR receipts
          </span>
          <div className="flex-1" />
          {paymentsQ.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
          <a
            href="/payments/check-run"
            onClick={(e) => {
              e.preventDefault();
              navigate({ to: "/payments/check-run" });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            Run Checks
          </a>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex flex-wrap items-end gap-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
            Type
          </label>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as PaymentDirection | "")}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">All payments</option>
            <option value="outgoing">Vendor payments (outgoing)</option>
            <option value="incoming">Customer receipts (incoming)</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          />
        </div>

        <div className="flex flex-col flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
            Search
          </label>
          <input
            ref={searchRef}
            type="text"
            placeholder="Payment #, reference, memo, check #…  (press / to focus)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          />
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400 self-end pb-1">
          {filtered.length} payment(s)
        </div>
      </div>

      {/* Grid — DataGridPro: sort, column resize, grouping, filter, export */}
      <div className="flex-1 px-6 py-4 overflow-hidden">
        {paymentsQ.error && (
          <div className="text-sm text-red-600 mb-3">
            Error: {(paymentsQ.error as Error).message}
          </div>
        )}
        <PaymentsGrid
          // The grid labels the party column Customer/Vendor by direction;
          // for the "All" view default to the AP (vendor) framing.
          direction={(direction || "outgoing") as PaymentDirection}
          payments={filtered}
          partiesById={partiesById}
          loading={paymentsQ.isLoading || partiesQ.isLoading}
        />
      </div>

      {/* Footer totals */}
      {filtered.length > 0 && (
        <div className="border-t border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-3 flex items-center gap-6 text-sm">
          {(direction === "" || direction === "outgoing") && (
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-2">
                Total AP (outgoing)
              </span>
              <span className="font-mono font-semibold text-red-700 dark:text-red-400">
                {fmt(totalOut)}
              </span>
            </div>
          )}
          {(direction === "" || direction === "incoming") && (
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-2">
                Total AR (incoming)
              </span>
              <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                {fmt(totalIn)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
